import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { ArrowUpRight } from 'lucide-react';
import { encodeIdLong as encodeId } from '../../lib/urlId';
import { useLocation, useNavigate } from 'react-router-dom';

// Composants personnalisés pour le markdown
import { trackEvent } from '../../lib/matomo';


const extractText = (node) => {
  if (typeof node === 'string') return node;
  if (!node || !node.props) return '';
  return React.Children.toArray(node.props.children)
    .map(extractText)
    .join('');
};

const renderWithAides = (children) => {
  const raw = React.Children.toArray(children)
    .map(extractText)
    .join('');

  if (!raw.includes('[AIDE:')) return null;

  const regex = /\[AIDE:(\{[\s\S]*?\})\]/g;
  const elements = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(raw))) {
    const before = raw.slice(lastIndex, match.index);
    if (before) elements.push(<span key={key++}>{before}</span>);

    try {
      const aide = JSON.parse(match[1]);
      elements.push(
        <AideCard
          key={key++}
          name={aide.name || aide.nom}
          id={aide.id}
          type={aide.type}
        />
      );
    } catch {
      elements.push(<span key={key++}>{match[0]}</span>);
    }

    lastIndex = match.index + match[0].length;
  }

  const after = raw.slice(lastIndex);
  if (after) elements.push(<span key={key++}>{after}</span>);

  return elements;
};



const AideCard = ({ name, id, type }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e) => {
    e.preventDefault();
    try {
      trackEvent('aide', 'clicked_in_chat', String(name).slice(0,150));
    } catch(_) {}

    navigate(`/aide/${encodeId(id)}`, {
      state: {
        from: {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
      },
    });
  };

  return (
    <a
      href={`/aide/${encodeId(id)}`}
      onClick={handleClick}
      className="block mb-2 rounded-xl border border-border/30 bg-card/60 backdrop-blur-sm transition-all duration-300 hover:translate-y-px hover:shadow-md hover:shadow-primary/30 overflow-hidden max-w-full"
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 max-w-full">
            <h4 className="text-sm sm:text-base font-semibold text-foreground break-words">
              {name}
            </h4>
            {type && (
              <span className="mt-1 inline-block px-2 py-0.5 rounded-md bg-primary/10 text-foreground border border-primary/20 text-xs font-medium">
                {type}
              </span>
            )}
          </div>
          <ArrowUpRight className="w-4 h-4 text-foreground/40 flex-shrink-0" />
        </div>
      </div>
      <div className="h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />
    </a>
  );
};

const CustomComponents = {
  aide: ({ node }) => {
    const { name, nom, id, type } = node.data;
    return <AideCard name={name || nom} id={id} type={type} />;
  },

  // Pour les listes à puces
  ul: ({ children, ...props }) => (
    <ul className="list-disc list-inside space-y-2 my-4 max-w-full" {...props}>
      {children}
    </ul>
  ),
  // Pour les listes numérotées avec IDs (comme dans votre exemple)
  ol: ({ children, ...props }) => (
    <ol className="list-decimal list-inside space-y-2 my-4 max-w-full" {...props}>
      {children}
    </ol>
  ),
  
  // Pour les éléments de liste avec mise en forme spéciale
  li: ({ children, ...props }) => {
      const content = renderWithAides(children);
      if (content) {
        return (
          <li className="mb-2 break-words max-w-full" {...props}>
            {content}
          </li>
        );
      }



    const text = React.Children.toArray(children).join('');

    // Détecter les patterns spéciaux comme "ID: 497"
    if (text.includes('ID:')) {
      const parts = text.split('(ID:');
      const title = parts[0].trim();
      const id = parts[1]?.replace(')', '').trim();
      
      return (
        <li className="flex items-start gap-3 p-3 bg-primary/5 rounded-lg border-l-4 border-primary/30 mb-2 max-w-full" {...props}>
          <div className="flex-1 min-w-0">
            <span className="font-medium text-foreground break-words">{title}</span>
            {id && (
              <span className="inline-block ml-2 px-2 py-1 bg-primary/10 text-foreground border border-primary/20 text-xs rounded-full font-medium">
                ID: {id}
              </span>
            )}
          </div>
        </li>
      );
    }
    
    return (
      <li className="text-foreground/90 mb-1 break-words max-w-full" {...props}>
        {children}
      </li>
    );
  },
  
  // Pour les paragraphes avec mise en forme spéciale
  p: ({ children, ...props }) => {
    const content = renderWithAides(children);
    if (content) {
    return (
      <p className="text-foreground/90 leading-relaxed my-3" {...props}>
        {content}
      </p>
    );
    }

    // Détecter les tags custom [AIDE:{...}]
    // if (text.includes('[AIDE:')) {
    //   try {
    //     const matches = Array.from(text.matchAll(/\[AIDE:(.*?)\]/g));
    //     if (matches.length > 0) {
    //       const aides = matches.map((m) => {
    //         const jsonPart = m[1].trim();
    //         try {
    //           return JSON.parse(jsonPart);
    //         } catch (e) {
    //           return null;
    //         }
    //       }).filter(Boolean);

    //       if (aides.length > 0) {
    //         return (
    //           <div className="my-3">
    //             {aides.map((aide, idx) => (
    //               <AideCard key={idx} name={aide.name || aide.nom} id={aide.id} type={aide.type} />
    //             ))} 
    //           </div>
    //         );
    //       }
    //     }
    //   } catch (e) {
    //     // En cas d'erreur de parsing, on retombe sur le rendu par défaut
    //   }
    // }

    // Détecter les messages d'aide
    // if (text.includes('Pour plus de détails') || text.includes('précisez votre')) {
    //   return (
    //     <div className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20 my-4">
    //       <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
    //       <p className="text-foreground text-sm" {...props}>
    //         {children}
    //       </p>
    //     </div>
    //   );
    // }
    
    return (
      <p className="text-foreground/90 leading-relaxed my-3" {...props}>
        {children}
      </p>
    );
  },
  
  // Pour les titres avec style amélioré
  h1: ({ children, ...props }) => (
    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 mt-6" {...props}>
      {children}
    </h1>
  ),
  
  h2: ({ children, ...props }) => (
    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-5" {...props}>
      {children}
    </h2>
  ),
  
  h3: ({ children, ...props }) => (
    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 mt-4" {...props}>
      {children}
    </h3>
  ),
  
  // Pour le texte en gras avec style amélioré
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-gray-900 dark:text-gray-100" {...props}>
      {children}
    </strong>
  ),
  
  // Pour les liens avec icône
  a: ({ children, href, ...props }) => (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
      {...props}
    >
      {children}
    </a>
  ),
  
  // Pour les blocs de code
  code: ({ children, className, ...props }) => {
    const isInline = !className;
    
    if (isInline) {
      return (
        <code className="px-1.5 py-0.5 bg-primary/10 text-foreground border border-primary/20 rounded text-sm font-mono" {...props}>
          {children}
        </code>
      );
    }
    
    return (
      <pre className="bg-primary/6 border border-primary/18 p-4 rounded-lg overflow-x-auto my-4">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    );
  },
  
  // Pour les tableaux
  table: ({ children, ...props }) => (
    <div className="overflow-x-auto my-4">
      <table className="w-full border-collapse border border-border/40" {...props}>
        {children}
      </table>
    </div>
  ),
  
  th: ({ children, ...props }) => (
    <th className="border border-border/40 px-4 py-2 bg-primary/8 font-semibold text-left text-foreground" {...props}>
      {children}
    </th>
  ),
  
  td: ({ children, ...props }) => (
    <td className="border border-border/40 px-4 py-2 text-foreground/90" {...props}>
      {children}
    </td>
  ),
  
  // Pour les blockquotes
  blockquote: ({ children, ...props }) => (
    <blockquote className="border-l-4 border-primary/40 pl-4 py-2 my-4 bg-primary/6 italic text-foreground/90" {...props}>
      {children}
    </blockquote>
  ),
};

// Composant principal
const MarkdownRenderer = ({ content, className = "" }) => {
  return (
    <div className={`markdown-content prose prose-sm dark:prose-invert max-w-full break-words overflow-x-hidden ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={CustomComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
